import * as XLSX from "xlsx";

/**
 * THE workbook parse. One implementation, called by both paths (#6892).
 *
 * ## Why this module exists
 *
 * The parse was written twice — once in `xlsxParse.worker.ts` and once as
 * `parseWorkbookInline`'s body — and the two copies were identical, which is
 * the state in which duplication is least visible and most dangerous.
 *
 * The specific hazard was not "they might drift". It was that a drift would
 * have been **invisible in CI by construction**: the worker copy is the one
 * that runs in browsers, and no test imports it. `canUseWorker()` is false
 * under jsdom, so every test exercises the inline copy and stubs a fake
 * `Worker`. The suite would have stayed green while the shipping path
 * changed under it.
 *
 * Extracting it also removes a real type-import cycle — `parseWorkbook.ts`
 * imported the message types from the worker, so the worker could not import
 * back. The message types live here now, so the worker file holds nothing but
 * message plumbing.
 */

/** The sheet the pipeline processed, if it recorded one. */
export interface XlsxParseRequest {
  buffer: ArrayBuffer;
  processSheetName?: string;
}

export type XlsxParseResponse = { ok: true; sheetName: string; rows: unknown[][] } | { ok: false; error: string };

/**
 * Sheet selection.
 *
 * An unknown `process_sheet_name` falls back to the first sheet rather than
 * failing: the pipeline's recorded name can be stale relative to a re-uploaded
 * file, and rendering the wrong sheet beats rendering no preview.
 */
export const resolveSheetName = (workbook: XLSX.WorkBook, processSheetName?: string): string => {
  if (processSheetName && workbook.SheetNames.includes(processSheetName)) {
    return processSheetName;
  }
  return workbook.SheetNames[0] || "Sheet1";
};

/**
 * Decodes a workbook to a 2D array of formatted cell strings.
 *
 * SYNCHRONOUS AND SLOW BY NATURE — 4.35 s on the 12.9 MB / 497,711-row
 * fixture, of which `XLSX.read` is 88%. Whoever calls it owns the thread it
 * blocks. That is why the worker exists and why the inline path is a
 * last-resort fallback.
 *
 * `raw: false` formats every cell to a string, which is what the preview
 * renders and what the highlight map keys on. Changing it changes displayed
 * values and what matches — hence one copy of these options, not two.
 */
export const parseWorkbookSheet = (
  buffer: ArrayBuffer,
  processSheetName?: string
): { sheetName: string; rows: unknown[][] } => {
  const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
  const sheetName = resolveSheetName(workbook, processSheetName);
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) {
    throw new Error(`Sheet "${sheetName}" not found in workbook`);
  }
  const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "", raw: false }) as unknown[][];
  return { sheetName, rows };
};
